/**
 * CROEvolution Listicle V1 - JavaScript
 * Countdown timer, progress bar, and FAQ accordion functionality
 */

(function() {
  'use strict';

  // Only run within the listicle section
  const section = document.querySelector('.croevolution-listicle-v1');
  if (!section) return;

  // ========== COUNTDOWN TIMER ==========
  function initCountdown() {
    const countdownEl = section.querySelector('.cro-countdown');
    if (!countdownEl) return;

    const hours = parseInt(countdownEl.dataset.countdownHours, 10) || 24;
    const storageKey = 'cro_listicle_countdown_end';
    
    // Get or set end time
    let endTime = localStorage.getItem(storageKey);
    if (!endTime) {
      endTime = Date.now() + (hours * 60 * 60 * 1000);
      localStorage.setItem(storageKey, endTime);
    } else {
      endTime = parseInt(endTime, 10);
    }

    const daysEl = countdownEl.querySelector('[data-days]');
    const hoursEl = countdownEl.querySelector('[data-hours]');
    const minutesEl = countdownEl.querySelector('[data-minutes]');
    const secondsEl = countdownEl.querySelector('[data-seconds]');

    function updateCountdown() {
      const now = Date.now();
      let diff = endTime - now;

      if (diff <= 0) {
        // Reset countdown when it ends
        endTime = Date.now() + (hours * 60 * 60 * 1000);
        localStorage.setItem(storageKey, endTime);
        diff = endTime - now;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hrs = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const secs = Math.floor((diff % (1000 * 60)) / 1000);

      if (daysEl) daysEl.textContent = String(days).padStart(2, '0');
      if (hoursEl) hoursEl.textContent = String(hrs).padStart(2, '0');
      if (minutesEl) minutesEl.textContent = String(mins).padStart(2, '0');
      if (secondsEl) secondsEl.textContent = String(secs).padStart(2, '0');
    }

    // Initial update
    updateCountdown();
    
    // Update every second
    setInterval(updateCountdown, 1000);
  }

  // ========== PROGRESS BAR ==========
  function initProgressBar() {
    const progressFill = section.querySelector('.cro-progress-bar__fill');
    const progressBar = section.querySelector('.cro-sticky-progress');
    
    if (!progressFill) {
      console.warn('CRO Listicle: Progress bar fill element not found');
      return;
    }

    // Get announcement height for fixed offset (recalculate dynamically)
    function getAnnouncementHeight() {
      const announcementEl = section.querySelector('.cro-sticky-top');
      if (announcementEl) {
        return announcementEl.offsetHeight;
      }
      return parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--cro-announcement-height') || '67'
      );
    }

    // Store original position for the progress bar
    let progressBarOriginalTop = null;
    let placeholderEl = null;
    let isInitialized = false;

    function calculateOriginalTop() {
      if (progressBar && !progressBar.classList.contains('is-fixed')) {
        // Get fresh measurements
        const rect = progressBar.getBoundingClientRect();
        progressBarOriginalTop = rect.top + window.scrollY;
        isInitialized = true;
      }
    }

    function updateProgressBarPosition() {
      if (!progressBar || !isInitialized || progressBarOriginalTop === null) return;
      
      const scrollTop = window.scrollY || window.pageYOffset;
      const announcementHeight = getAnnouncementHeight();
      
      // When the top of progress bar would scroll past the bottom of fixed announcement
      // Add a small buffer (2px) to prevent any overlap flicker
      const threshold = progressBarOriginalTop - announcementHeight - 2;

      if (scrollTop >= threshold && !progressBar.classList.contains('is-fixed')) {
        // Create placeholder to prevent layout shift
        if (!placeholderEl) {
          placeholderEl = document.createElement('div');
          placeholderEl.style.height = progressBar.offsetHeight + 'px';
          placeholderEl.className = 'cro-progress-placeholder';
          progressBar.parentNode.insertBefore(placeholderEl, progressBar.nextSibling);
        }
        // Switch to fixed positioning via class
        progressBar.classList.add('is-fixed');
      } else if (scrollTop < threshold && progressBar.classList.contains('is-fixed')) {
        // Remove fixed positioning
        progressBar.classList.remove('is-fixed');
        // Remove placeholder
        if (placeholderEl) {
          placeholderEl.remove();
          placeholderEl = null;
        }
      }
    }

    function updateProgress() {
      const scrollTop = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
      const docHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight
      ) - window.innerHeight;
      
      if (docHeight <= 0) {
        progressFill.style.width = '2%';
        return;
      }
      
      const progress = Math.max(2, Math.min((scrollTop / docHeight) * 100, 100));
      progressFill.style.width = progress + '%';

      // Also update position
      updateProgressBarPosition();
    }

    // Calculate original position after layout is complete
    // Use multiple attempts to ensure layout is stable
    function initPosition() {
      calculateOriginalTop();
      // Double-check after a short delay to catch any late layout shifts
      setTimeout(function() {
        if (!progressBar.classList.contains('is-fixed')) {
          calculateOriginalTop();
        }
        updateProgress();
      }, 50);
    }

    // Wait for fonts and images to load which can shift layout
    if (document.readyState === 'complete') {
      setTimeout(initPosition, 50);
    } else {
      window.addEventListener('load', function() {
        setTimeout(initPosition, 50);
      });
      // Also try earlier in case load event already fired
      setTimeout(initPosition, 150);
    }

    // Update on scroll
    window.addEventListener('scroll', updateProgress, { passive: true });
    
    // Update on resize (recalculate positions)
    window.addEventListener('resize', function() {
      // Reset to calculate true position
      if (progressBar.classList.contains('is-fixed')) {
        progressBar.classList.remove('is-fixed');
        if (placeholderEl) {
          placeholderEl.remove();
          placeholderEl = null;
        }
      }
      isInitialized = false;
      setTimeout(function() {
        calculateOriginalTop();
        updateProgress();
      }, 100);
    }, { passive: true });

    // Initial update
    updateProgress();
  }

  // ========== FAQ ACCORDION ==========
  function initFAQ() {
    const faqItems = section.querySelectorAll('.cro-faq__item');
    faqItems.forEach(item => {
      item.addEventListener('toggle', function() {
        // Optional: close other items when one opens
        // Uncomment below for accordion behavior
        /*
        if (this.open) {
          faqItems.forEach(other => {
            if (other !== this) other.open = false;
          });
        }
        */
      });
    });
  }

  // ========== STICKY MOBILE CTA ==========
  function initMobileStickyCta() {
    const stickyCta = document.querySelector('.cro-mobile-sticky-cta');
    if (!stickyCta) return;

    // Find all offer CTA sections on the page
    const offerCtaSections = document.querySelectorAll('.cro-offer-cta');
    if (!offerCtaSections.length) return;

    function checkVisibility() {
      const viewportHeight = window.innerHeight;
      let shouldHide = false;

      // Check if any offer CTA is in the viewport
      offerCtaSections.forEach(function(offerCta) {
        const rect = offerCta.getBoundingClientRect();
        // Hide when offer CTA enters viewport (with some buffer)
        // Show when offer CTA is completely out of viewport
        if (rect.top < viewportHeight - 50 && rect.bottom > 100) {
          shouldHide = true;
        }
      });

      if (shouldHide) {
        stickyCta.classList.add('is-hidden');
      } else {
        stickyCta.classList.remove('is-hidden');
      }
    }

    // Check on scroll
    window.addEventListener('scroll', checkVisibility, { passive: true });
    
    // Check on resize
    window.addEventListener('resize', checkVisibility, { passive: true });

    // Initial check
    checkVisibility();
  }

  // Initialize
  function init() {
    initCountdown();
    initProgressBar();
    initFAQ();
    initMobileStickyCta();
  }

  document.addEventListener('DOMContentLoaded', init);

  // Also init if DOM already loaded
  if (document.readyState !== 'loading') {
    init();
  }
})();
